import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Pencil, Trash2 } from 'lucide-react';

import { Drawer } from '../../components/Drawer/Drawer';
import { useToast } from '../../components/Toast';
import { formatHebrewRelative } from '../../lib/relativeTime';
import {
  DOC_TYPE_LABELS,
  entityPath,
  sourceLabel,
  useSoftDeleteDocument,
  useUpdateDocument,
  type DocumentRow,
} from './documentsQueries';
import styles from './documents.module.css';

interface DocumentDrawerProps {
  open: boolean;
  doc: DocumentRow | null;
  entityName: string | null;
  orgId: string | null;
  onClose: () => void;
}

export function DocumentDrawer({ open, doc, entityName, orgId, onClose }: DocumentDrawerProps) {
  const toast = useToast();
  const update = useUpdateDocument(orgId);
  const softDelete = useSoftDeleteDocument(orgId);

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const docId = doc?.id ?? null;

  useEffect(() => {
    setEditing(false);
    setTitle(doc?.title ?? '');
    setBody(doc?.body ?? '');
  }, [docId, doc?.title, doc?.body]);

  if (!doc) return null;

  const link = entityPath(doc.entity_type, doc.entity_id);

  async function handleSave() {
    if (!docId) return;
    if (!title.trim()) {
      toast.error('כותרת חסרה');
      return;
    }
    try {
      await update.mutateAsync({ id: docId, values: { title, body } });
      toast.success('נשמר');
      setEditing(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'השמירה נכשלה');
    }
  }

  async function handleDelete() {
    if (!docId) return;
    if (!window.confirm(`למחוק את "${title}"?`)) return;
    try {
      await softDelete.mutateAsync(docId);
      toast.success('נמחק');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'המחיקה נכשלה');
    }
  }

  return (
    <Drawer
      open={open}
      eyebrow={DOC_TYPE_LABELS[doc.type]}
      title={editing ? 'ערוך מסמך' : doc.title}
      onClose={onClose}
      footer={
        editing ? (
          <>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => {
                setTitle(doc.title);
                setBody(doc.body);
                setEditing(false);
              }}
            >
              בטל
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              disabled={update.isPending || !title.trim()}
              onClick={() => {
                void handleSave();
              }}
            >
              שמור
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={styles.btnDanger}
              disabled={softDelete.isPending}
              onClick={() => {
                void handleDelete();
              }}
            >
              <Trash2 size={14} aria-hidden="true" />
              מחק
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => {
                setEditing(true);
              }}
            >
              <Pencil size={14} aria-hidden="true" />
              ערוך
            </button>
          </>
        )
      }
    >
      <dl className={styles.meta}>
        <div>
          <dt>נוצר</dt>
          <dd>
            <span className="num">{formatHebrewRelative(doc.created_at)}</span>
          </dd>
        </div>
        <div>
          <dt>מקור</dt>
          <dd>{sourceLabel(doc.source)}</dd>
        </div>
        <div>
          <dt>משויך ל</dt>
          <dd>
            {entityName === null ? <span className={styles.dim}>ללא שיוך</span> : null}
            {entityName !== null && link === null ? entityName : null}
            {entityName !== null && link !== null ? (
              <Link to={link} className={styles.entityLink} onClick={onClose}>
                {entityName}
                <ExternalLink size={12} aria-hidden="true" />
              </Link>
            ) : null}
          </dd>
        </div>
      </dl>

      {editing ? (
        <div className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="doc-title">כותרת</label>
            <input
              id="doc-title"
              type="text"
              className={styles.input}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="doc-body">תוכן</label>
            <textarea
              id="doc-body"
              className={styles.textarea}
              rows={16}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
              }}
            />
          </div>
        </div>
      ) : doc.body.trim() ? (
        <p className={styles.bodyText}>{doc.body}</p>
      ) : (
        <p className={styles.dim}>אין תוכן. ערוך והוסף.</p>
      )}
    </Drawer>
  );
}
